import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CombinedTable, Reservation } from "@/types/table";
import { Users, ArrowRightLeft, Check, Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCustomerName } from "@/lib/clientUtils";

export interface CombinedTableOption {
  combinedTable: CombinedTable;
  isOccupied: boolean;
  existingReservation?: Reservation;
  capacityMatch: 'optimal' | 'acceptable' | 'under';
}

interface CombinedTableSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draggedReservation: {
    id: string;
    customerFirstName: string;
    customerLastName: string;
    numberOfPeople: number;
    tableId?: string | null;
    combinedTableId?: string | null;
  } | null;
  options: CombinedTableOption[];
  onSelect: (combinedTableId: string, swapWithReservationId?: string) => void;
  onSelectSingleTable?: (tableId: string) => void;
  targetTableId?: string | null;
  targetTableName?: string | null;
}

export function CombinedTableSelectionDialog({
  open,
  onOpenChange,
  draggedReservation,
  options,
  onSelect,
  onSelectSingleTable,
  targetTableId,
  targetTableName,
}: CombinedTableSelectionDialogProps) {
  if (!draggedReservation) return null;

  const numberOfPeople = draggedReservation.numberOfPeople;
  
  // Separate available and occupied options
  const availableOptions = options.filter(opt => !opt.isOccupied);
  const occupiedOptions = options.filter(opt => opt.isOccupied);

  const getCapacityBadge = (capacity: number, capacityMatch: CombinedTableOption['capacityMatch']) => {
    const matchesClass = capacityMatch === 'optimal' 
      ? 'bg-green-500/20 text-green-700 border-green-500/30'
      : capacityMatch === 'acceptable'
        ? 'bg-yellow-500/20 text-yellow-700 border-yellow-500/30'
        : 'bg-red-500/20 text-red-700 border-red-500/30';
    
    return (
      <Badge variant="outline" className={cn("text-xs", matchesClass)}>
        <Users className="w-3 h-3 mr-1" />
        {capacity} places
      </Badge>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-purple-500" />
            Choisir une table
          </DialogTitle>
          <DialogDescription>
            La table {targetTableName} fait partie de plusieurs configurations.
            Où souhaitez-vous placer la réservation de {formatCustomerName(draggedReservation.customerFirstName, draggedReservation.customerLastName)} ({numberOfPeople} pers.) ?
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px]">
          <div className="space-y-4 py-2">
            {/* Option for single table if available */}
            {targetTableId && onSelectSingleTable && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Table individuelle</h4>
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto py-3 px-4"
                  onClick={() => {
                    onSelectSingleTable(targetTableId);
                    onOpenChange(false);
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                        <Check className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <span className="font-medium">{targetTableName}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">Table seule</span>
                  </div>
                </Button>
              </div>
            )}

            {/* Available combined tables */}
            {availableOptions.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Tables combinées disponibles</h4>
                {availableOptions.map((option) => (
                  <Button
                    key={option.combinedTable.id}
                    variant="outline"
                    className="w-full justify-start h-auto py-3 px-4 hover:bg-purple-50 hover:border-purple-300 dark:hover:bg-purple-950/20"
                    onClick={() => {
                      onSelect(option.combinedTable.id);
                      onOpenChange(false);
                    }}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                          <Layers className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium">{option.combinedTable.name}</p>
                        </div>
                      </div>
                      {getCapacityBadge(option.combinedTable.totalCapacity, option.capacityMatch)}
                    </div>
                  </Button>
                ))}
              </div>
            )}

            {/* Occupied combined tables (swap possible) */}
            {occupiedOptions.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <ArrowRightLeft className="w-4 h-4" />
                  Échanger avec une réservation existante
                </h4>
                {occupiedOptions.map((option) => (
                  <Button
                    key={option.combinedTable.id}
                    variant="outline"
                    className="w-full justify-start h-auto py-3 px-4 hover:bg-orange-50 hover:border-orange-300 dark:hover:bg-orange-950/20"
                    onClick={() => {
                      onSelect(option.combinedTable.id, option.existingReservation?.id);
                      onOpenChange(false);
                    }}
                  >
                    <div className="flex flex-col w-full gap-2">
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                            <ArrowRightLeft className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                          </div>
                          <div className="text-left">
                            <p className="font-medium">{option.combinedTable.name}</p>
                          </div>
                        </div>
                        {getCapacityBadge(option.combinedTable.totalCapacity, option.capacityMatch)}
                      </div>
                      {option.existingReservation && (
                        <div className="ml-11 text-left">
                          <p className="text-xs text-muted-foreground">
                            Actuellement occupée par{' '}
                            <span className="font-medium text-foreground">
                              {formatCustomerName(option.existingReservation.customerFirstName, option.existingReservation.customerLastName)}
                            </span>
                            {' '}({option.existingReservation.numberOfPeople} pers.)
                          </p>
                          <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">
                            → Cette réservation sera déplacée vers {draggedReservation.tableId ? 'votre table d\'origine' : 'une autre table'}
                          </p>
                        </div>
                      )}
                    </div>
                  </Button>
                ))}
              </div>
            )}

            {availableOptions.length === 0 && occupiedOptions.length === 0 && !targetTableId && (
              <div className="text-center py-4 text-muted-foreground">
                Aucune table combinée disponible pour cette configuration.
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
